// Packages
import React, { useState, useEffect, useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

// MUI
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Divider,
} from "@mui/material";

// Components
import TableField from "components/Table";
import PendingChildren from "./components/PendingChildren";
import GlobalAlert from "components/Notification";
import { SolidButton } from "components/buttons/SolidButton";
import SearchInput from "components/SearchInput";
import CreateAndEditModal from './Modal/CreateAndEditModal';
import RemovalModal from "./Modal/RemovalModal";

// IMG
import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

// Helper
import {
  getMainSendRequestParams,
  getInitTableData,
  getCleanPayload,
} from "./helper";

// Const
import {
  defaultAlertInfo,
  getMainTableColumns,
  defaultActions,
} from "./const";

const RadiusServer = ({
  showEditGeneral
}) => {
  const [uuids] = useState(() => ({
    read: uuidv4(),
    create: uuidv4(),
    update: uuidv4(),
    remove: uuidv4(),
  }))
  const theme = useTheme();
  const columnHelper = createColumnHelper();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();

  const [alertInfo, setAlertInfo] = useState(structuredClone(defaultAlertInfo));
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  const [actions, setActions] = useState(defaultActions);
  const [selectedRadius, setSelectedRadius] = useState(null);

  const mainColumns = useMemo(() => {
    return getMainTableColumns({
      auth,
      columnHelper,
      permissions: auth.permissions,
      theme,
      mainTableCallbacks: {
        onEdit: selectedTarget => {
          setSelectedRadius(selectedTarget);
          setActions({ ...defaultActions, isEdit: true });
        },
        onRemove: selectedTarget => {
          setSelectedRadius(selectedTarget);
          setActions({ ...defaultActions, isRemove: true });
        }
      }
    })
  }, [auth, theme]);

  const mainTableInstance = useTableInstance({
    tableName: "RadiusServer",
    data: tableData,
    columns: mainColumns,
    enableSorting: true,
    enableColumnOrder: false,
    enableRowSelection: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["name", "authenticateServer", "accountingServer", "referencedServices"],
  });

  const fetchMainTableData = () => {
    setIsLoading(true);

    if (isAuthReady) {
      const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "read" });

      sendRequest(
        apiUrl,
        uuids.read,
        requestParams
      );
    }
  }

  const onCreate = () => {
    setIsLoading(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "create" });
    const payload = getCleanPayload({ selectedRadius });

    sendRequest(
      apiUrl,
      uuids.create,
      requestParams,
      payload
    );
  }

  const onUpdate = () => {
    setIsLoading(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "update", params: { radiusId: selectedRadius.id } });
    const payload = getCleanPayload({ selectedRadius });

    sendRequest(
      apiUrl,
      uuids.update,
      requestParams,
      payload
    );
  }

  const onRemove = () => {
    setIsLoading(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "delete" });
    const payload = {
      ids: [selectedRadius.id]
    };

    sendRequest(
      apiUrl,
      uuids.remove,
      requestParams,
      payload
    );
  }

  useEffect(() => {
    fetchMainTableData();
  }, [isAuthReady]);

  useEffect(() => {
    if (actions.isCreateAndSave) {
      onCreate();
    } else if (actions.isEditAndSave) {
      onUpdate();
    } else if (actions.isRemoveAndSave) {
      onRemove();
    }
  }, [actions])

  useEffect(() => {
    if (!dataMap?.[uuids.read]) return;

    const response = getData(uuids.read);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to get the RADIUS server list.",
        });
      } else {

        const initTableData = getInitTableData({
          responseData
        });

        setTableData(initTableData);
      }

      setIsLoading(false);
    }
  }, [dataMap?.[uuids.read]]);

  useEffect(() => {
    if (!dataMap?.[uuids.create]) return;

    const response = getData(uuids.create);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to create the RADIUS server list.",
        });
        setActions({ ...defaultActions, isCreate: true, isCreateAndSave: false });
      } else {
        fetchMainTableData();
        setActions(defaultActions);
      }

      setSelectedRadius(null);
      setIsLoading(false);
    }
  }, [dataMap?.[uuids.create]]);

  useEffect(() => {
    if (!dataMap?.[uuids.update]) return;

    const response = getData(uuids.update);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to update the RADIUS server list.",
        });
        setActions({ ...defaultActions, isEdit: true, isEditAndSave: false });
      } else {
        fetchMainTableData();
        setActions(defaultActions);
      }

      setSelectedRadius(null);
      setIsLoading(false);
    }
  }, [dataMap?.[uuids.update]]);

  useEffect(() => {
    if (!dataMap?.[uuids.remove]) return;

    const response = getData(uuids.remove);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to remove the RADIUS server list.",
        });
      } else {
        fetchMainTableData();
      }

      setActions(defaultActions);
      setSelectedRadius(null);
      setIsLoading(false);
    }
  }, [dataMap?.[uuids.remove]]);

  return (
    <>
      <Box sx={{ padding: "20px" }}>

        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              height: "80px",
              gap: 3
            }}
          >
            <Divider
              sx={{ width: "1px", height: "40px", margin: "20px 0" }}
              orientation="vertical"
              flexItem
            />

            {/* Search */}
            <SearchInput
              value={mainTableInstance.globalFilter}
              onChange={mainTableInstance.setGlobalFilter}
              placeholder="Search by RADIUS Server, Name"
            />

            <SolidButton
              sx={{ whiteSpace: "nowrap" }}
              startIcon={<AddIcon />}
              width="auto"
              height="35px"
              onClick={() => setActions({ ...defaultActions, isCreate: true })}
            >
              Create RADIUS Profile
            </SolidButton>
          </Box>
        </Box>

        <TableField
          {...mainTableInstance}
          pendingChildren={
            <PendingChildren
              {...{
                isLoading,
                list: tableData,
                description: "No data"
              }}
            />
          }
          handleRowOnClick={() => { }}
        />

      </Box>

      {
        (actions.isCreate || actions.isEdit) &&
        <CreateAndEditModal
          {...{
            selectedRadius,
            setSelectedRadius,
            actions,
            setActions,
            showEditGeneral,
          }}

        />
      }

      {
        selectedRadius &&
        actions.isRemove &&
        <RemovalModal
          {...{
            isOpenRemovalModal: actions.isRemove,
            selectedRadius,
            removeCallbacks: {
              removeCancel: () => {
                setSelectedRadius(null);
                setActions({ ...defaultActions, isRemove: false });
              },
              removeConfirm: () => {
                setActions({ ...defaultActions, isRemoveAndSave: true });
              },
            },
          }}
        />
      }

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo(structuredClone(defaultAlertInfo))}
      />
    </>
  );
};

export default RadiusServer;
